import React from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';
import {
  InputWithLabel,
  TextareaWithLabel,
  Checkbox,
  RadioGroup,
  RadioButton,
  Dropdown,
  SearchInputSelection,
  MultiSelectDropdown,
  DateInput,
  DateTimeInput,
  Label,
  Text,
} from '@tasama/ui';
import DOMPurify from 'dompurify';
import type { DynamicFieldType, FormFieldSchema } from '@tasama/types';
import { DynamicFileUploadField } from './DynamicFileUploadField';
import { stripFieldLabelHtml } from './strip-field-label-html.util';

export type FieldWiringMode =
  | 'register'
  | 'controller'
  | 'custom'
  | 'display'
  | 'inject'
  | 'divider';

interface RegistryEntry {
  mode: FieldWiringMode;
}

// FE-only type -> component + RHF wiring mode registry, keyed on BFF's real normalized
// vocabulary (not raw SNOW friendly_type names — see form.types.ts DynamicFieldType).
// Only 'text'/'textarea'/'select'/'checkbox'/'datetime'/'label' keys are confirmed from a
// real BFF response; the rest are PROVISIONAL guesses — see BE_CONTRACT_OPEN_QUESTIONS.md.
export const FIELD_REGISTRY: Record<DynamicFieldType, RegistryEntry> = {
  text: { mode: 'register' }, // confirmed
  textarea: { mode: 'register' }, // confirmed
  select: { mode: 'controller' }, // confirmed
  checkbox: { mode: 'register' }, // confirmed
  datetime: { mode: 'controller' }, // confirmed — DateInput forwards no external ref, register's ref never binds
  label: { mode: 'display' }, // confirmed
  hidden: { mode: 'inject' }, // confirmed — server-injected, never rendered
  radio: { mode: 'controller' }, // PROVISIONAL
  multiselect: { mode: 'controller' }, // PROVISIONAL
  email: { mode: 'register' }, // PROVISIONAL
  url: { mode: 'register' }, // PROVISIONAL
  yes_no: { mode: 'controller' }, // confirmed — reuses RadioGroup, no Toggle/Switch exists in @tasama/ui
  date: { mode: 'controller' }, // PROVISIONAL
  lookupSelect: { mode: 'controller' }, // PROVISIONAL — TODO: BE must pre-resolve `choices`, SearchInputSelection has no async fetch
  file: { mode: 'custom' }, // confirmed — wired to DynamicFileUploadField
  attachment: { mode: 'custom' }, // PROVISIONAL — TODO: DocumentUploadSection's files/onFilesSelected contract; unconfirmed whether this is a distinct type from 'file'
  richTextLabel: { mode: 'display' }, // PROVISIONAL
  html: { mode: 'display' }, // PROVISIONAL
  container_split: { mode: 'divider' }, // confirmed — renders as a plain divider line
};

interface RenderFieldControlProps {
  field: FormFieldSchema;
  register: UseFormRegister<Record<string, unknown>>;
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  /** Namespaced translator, so this module holds no user-facing English of its own. */
  t: (_key: string) => string;
  /**
   * A blocking message the caller derived from live form values (a violated VALIDATE rule or a
   * failed input pattern). Used only when react-hook-form has not produced an error of its own,
   * which for Controller-wired controls is always. (TDF-1410 FE-2)
   */
  ruleErrorMessage?: string | undefined;
  testID: string;
}

// Field-level error message, if any — zodResolver reports it under formState.errors
// keyed by field.name for both registered and Controller-wired fields.
function getFieldError(
  errors: FieldErrors<Record<string, unknown>>,
  name: string
): string | undefined {
  const message = errors[name]?.message;
  return typeof message === 'string' ? message : undefined;
}

// Resolves one FormFieldSchema to its real @tasama/ui component, wired per FIELD_REGISTRY's mode.
export function renderFieldControl({
  field,
  register,
  control,
  errors,
  t,
  ruleErrorMessage,
  testID,
}: RenderFieldControlProps): React.ReactNode {
  const { mode } = FIELD_REGISTRY[field.type];
  const errorMessage = getFieldError(errors, field.name) ?? ruleErrorMessage;
  const hasError = !!errorMessage;
  // Only the 'display' branch below needs the raw HTML; every other mode uses this as a
  // plain-text label prop.
  const safeLabel = stripFieldLabelHtml(field.label);

  if (mode === 'divider') {
    return <hr className="border-t border-gray-400" />;
  }

  if (mode === 'display' || mode === 'inject') {
    // 'label'/'html' fields carry SNOW rich-text HTML; render it sanitized (inline
    // styles stripped, portal typography applied) instead of showing the raw markup.
    if (mode !== 'display') return null;
    return (
      <div
        className="text-fg-secondary [&_p]:text-sm [&_p]:font-normal [&_p]:leading-[20px] [&_strong]:font-semibold"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(field.label ?? '', {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em'],
            ALLOWED_ATTR: [],
          }),
        }}
      />
    );
  }

  if (mode === 'custom') {
    if (field.type === 'file') {
      return (
        <DynamicFileUploadField
          field={field}
          control={control}
          testID={testID}
        />
      );
    }
    // 'attachment' — PROVISIONAL, unconfirmed by BE, not implemented yet
    return null;
  }

  if (mode === 'register') {
    if (field.type === 'checkbox') {
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            id={field.name}
            disabled={field.readOnly}
            testID={testID}
            {...register(field.name)}
          />
          <label
            htmlFor={field.name}
            className="text-sm font-normal text-fg-secondary"
          >
            {safeLabel}
          </label>
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <TextareaWithLabel
          id={field.name}
          label={safeLabel}
          required={field.mandatory}
          disabled={field.readOnly}
          error={hasError}
          errorMessage={errorMessage}
          testID={testID}
          {...register(field.name)}
        />
      );
    }
    return (
      <InputWithLabel
        id={field.name}
        label={safeLabel}
        required={field.mandatory}
        disabled={field.readOnly}
        type={
          field.type === 'email'
            ? 'email'
            : field.type === 'url'
              ? 'url'
              : 'text'
        }
        error={hasError}
        errorMessage={errorMessage}
        testID={testID}
        {...register(field.name)}
      />
    );
  }

  // mode === 'controller'
  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: rhf }) => {
        if (field.type === 'select') {
          return (
            <Dropdown
              testID={testID}
              label={safeLabel}
              options={(field.choices ?? []).map((c) => ({
                value: c.value,
                label: c.label,
              }))}
              value={(rhf.value as string) ?? ''}
              onChange={rhf.onChange}
              required={!!field.mandatory}
              disabled={field.readOnly}
              error={hasError}
              errorMessage={errorMessage}
            />
          );
        }
        if (field.type === 'lookupSelect') {
          return (
            <SearchInputSelection
              testID={testID}
              label={safeLabel}
              options={(field.choices ?? []).map((c) => ({
                value: c.value,
                label: c.label,
              }))}
              value={(rhf.value as string) ?? ''}
              onChange={rhf.onChange}
              disabled={!!field.readOnly}
              error={hasError}
              errorMessage={errorMessage}
            />
          );
        }
        if (field.type === 'multiselect') {
          return (
            <MultiSelectDropdown
              testID={testID}
              label={safeLabel}
              options={(field.choices ?? []).map((c) => ({
                value: c.value,
                label: c.label,
              }))}
              value={(rhf.value as string[]) ?? []}
              onChange={rhf.onChange}
              disabled={!!field.readOnly}
              error={hasError}
              {...(errorMessage ? { errorMessage } : {})}
            />
          );
        }
        if (field.type === 'yes_no') {
          // Matches the compact inline layout used for Yes/No questions elsewhere
          // (see onboarding's ServiceNeeds "Is there already an allocated budget?"
          // pattern) — RadioButton atoms laid out horizontally, not RadioGroup's
          // bordered-card option style.
          const options = [
            { value: 'true', label: t('common:labels.yes') },
            { value: 'false', label: t('common:labels.no') },
          ];
          return (
            <div className="flex flex-col">
              <Text className="mb-2 text-sm font-normal text-fg-secondary">
                {safeLabel}
              </Text>
              <div className="flex gap-6">
                {options.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <RadioButton
                      name={field.name}
                      value={option.value}
                      checked={rhf.value === option.value}
                      onChange={(e) => rhf.onChange(e.target.value)}
                      disabled={!!field.readOnly}
                      uiSize="sm"
                      testID={`${testID}-${option.value}`}
                    />
                    <Text className="text-sm font-normal text-fg-secondary">
                      {option.label}
                    </Text>
                  </label>
                ))}
              </div>
              {hasError && (
                <Text className="mt-1 text-sm text-fg-error-primary">
                  {errorMessage}
                </Text>
              )}
            </div>
          );
        }
        if (field.type === 'radio') {
          const options = (field.choices ?? []).map((c) => ({
            value: c.value,
            label: c.label,
          }));
          return (
            <div className="space-y-sm">
              {/* RadioGroup has no built-in label prop (unlike Dropdown/InputWithLabel),
                  so the field label is rendered here instead. */}
              <Label
                className={
                  field.readOnly
                    ? 'block text-disabled'
                    : 'block text-fg-secondary'
                }
              >
                {safeLabel}
              </Label>
              <RadioGroup
                testID={testID}
                options={options}
                value={rhf.value as string}
                onChange={rhf.onChange}
                required={!!field.mandatory}
                disabled={!!field.readOnly}
              />
            </div>
          );
        }
        if (field.type === 'datetime') {
          return (
            <DateTimeInput
              id={field.name}
              label={safeLabel}
              required={field.mandatory}
              disabled={field.readOnly}
              testID={testID}
              value={(rhf.value as string) ?? ''}
              onChange={rhf.onChange}
              error={hasError}
              errorMessage={errorMessage}
            />
          );
        }
        // date
        return (
          <DateInput
            id={field.name}
            label={safeLabel}
            required={field.mandatory}
            disabled={field.readOnly}
            testID={testID}
            value={(rhf.value as string) ?? ''}
            onChange={rhf.onChange}
            error={hasError}
            errorMessage={errorMessage}
            // Wide range so date-of-birth style fields can reach far into the past
            // (the default ~10-year window stopped at ~2016). (TDF-1374)
            startMonth={new Date(new Date().getFullYear() - 100, 0, 1)}
            endMonth={new Date(new Date().getFullYear() + 10, 11, 31)}
          />
        );
      }}
    />
  );
}
